import { useEffect } from 'react';
import { CampaignBillingSummary as CampaignSummaryType, CampaignBillingSummary as CampaignBillingSummaryType, CampaignBillingChannelData } from '../../types/campaigns.type';
import GetCampaignBillingSummary from '../../services/adapters/campaignBillingSummary';
import { ColumnDef } from '../../types/table.type';
import useRequestState from '../../hooks/useRequestState';
import useTableSort from '../../hooks/useTableSort';

interface CampaignBillingSummaryTableProps {
  refreshTrigger: number;
  onOrganisationSelect: (organisationName: string) => void;
  selectedOrganisationName: string | null;
}

export default function CampaignBillingSummary({
  refreshTrigger,
  onOrganisationSelect,
  selectedOrganisationName,
}: CampaignBillingSummaryTableProps) {
  const {
    data: billingData,
    error: billingDataError,
    loading,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<CampaignBillingSummaryType[]>();
  const { sortedData, handleSort } = useTableSort<CampaignSummaryType>(billingData || []);

  useEffect(() => {
    const fetchBillingData = async () => {
      const adapter = new GetCampaignBillingSummary();
      const response = adapter.adaptResponse();
      await handleRequest(response);
    };
    void fetchBillingData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  if (loading) {
    return (
      <div className="border border-pipgrey-light rounded-md p-4">
        <p className="text-sm text-secondary">Loading current allowances by client...</p>
      </div>
    );
  }

  if (billingDataError) {
    return (
      <div className="p-4 text-red-600">
        {billingDataError.message}
      </div>
    );
  }

  const getTableRowBackgroundColourClass = (organisationName: string, index: number) => {
    const isSelected = selectedOrganisationName === organisationName;

    if (isSelected) {
      return 'bg-primary';
    }
    if (index % 2 === 0) {
      return 'bg-white';
    }
    return 'bg-gray-50';
  };

  const columns: ColumnDef<CampaignSummaryType>[] = [
    { key: 'organisationName', header: 'Organisation Name', sortable: true },
    { key: 'isCampaignEnabled', header: 'Campaign Enabled', sortable: true },
    {
      key: 'printAndPost',
      header: 'Letter',
      sortable: false,
      render: (value: CampaignBillingChannelData) => value.allowance.toLocaleString(),
    },
    {
      key: 'printAndPost',
      header: 'Last Updated',
      sortable: true,
      render: (value: CampaignBillingChannelData) => value.lastUpdated,
    },
    {
      key: 'sms',
      header: 'SMS',
      sortable: false,
      render: (value: CampaignBillingChannelData) => value.allowance.toLocaleString(),
    },
    {
      key: 'sms',
      header: 'Last Updated',
      sortable: true,
      render: (value: CampaignBillingChannelData) => value.lastUpdated,
    },
  ];

  return (
    <div className="border border-pipgrey-light rounded-md overflow-hidden p-2">
      <h3 className="m-2 font-nunito">Remaining Allowances</h3>
      <div className="h-100 m-2 overflow-y-auto">
        <FallbackWrapper loadingMessage="Loading current allowances by client...">
          {billingData && (
          <table className="w-full">
            <thead className="bg-pipgrey-light sticky top-0">
              <tr>
                {columns.map((column) => (
                  <th
                    key={`${String(column.key)}-${column.header}`}
                    scope="col"
                    title={column.sortable !== false ? `Click to sort by ${column.header}` : column.header}
                    className={`text-left px-3 py-2 font-normal text-sm ${column.sortable ? 'cursor-pointer' : ''} ${column.headerClassName || ''}`}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        className="cursor-pointer"
                        onClick={() => handleSort(String(column.key))}
                      >
                        {column.header}
                      </button>
                    ) : column.header}

                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, index) => (
                <tr
                  key={row.organisationName}
                  onClick={() => onOrganisationSelect(row.organisationName)}
                  className={
                  `border-b border-pipgrey-light cursor-pointer hover:bg-primary transition-colors ${
                    getTableRowBackgroundColourClass(row.organisationName, index)}`
                }
                >
                  <td className="px-3 py-1.5 text-sm">{row.organisationName}</td>
                  <td className="px-3 py-1.5 text-sm">{row.isCampaignEnabled ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-1.5 text-sm">{row.printAndPost.allowance.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-sm whitespace-nowrap">{row.printAndPost.lastUpdated}</td>
                  <td className="px-3 py-1.5 text-sm">{row.sms.allowance.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-sm whitespace-nowrap">{row.sms.lastUpdated}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </FallbackWrapper>

      </div>
    </div>
  );
}
